/**
 * @fileoverview Service Worker Manager - Service Worker管理システム
 */

import { MinimalErrorTracker } from '../shared/log.js';

let registration = null;
let navigatorTarget = null;
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let updatePendingReload = false;
let controllerChangeListener = null;
let onlineListenersAttached = false;
let windowTarget = typeof window !== 'undefined' ? window : null;
let onlineHandler = null;
let offlineHandler = null;
const updateListeners = new Set();
let updateAvailable = false;

function handleControllerChange() {
  // updatePendingReload フラグで確実に1回だけリロード
  // （controllerchange イベントが複数回発火しても安全）
  if (!updatePendingReload) {
    return;
  }

  updatePendingReload = false;

  if (windowTarget && windowTarget.location && typeof windowTarget.location.reload === 'function') {
    windowTarget.location.reload();
  }
}

function activateWaitingWorker() {
  if (!registration || !registration.waiting) {
    return false;
  }

  try {
    ensureControllerChangeListener(navigatorTarget, windowTarget);
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    updatePendingReload = true;
    updateAvailable = false;
    MinimalErrorTracker.info('SYSTEM', 'Service Worker activation requested');
    return true;
  } catch (error) {
    MinimalErrorTracker.warn('SYSTEM', 'Failed to activate waiting Service Worker', error);
    return false;
  }
}

function notifyUpdateAvailable() {
  updateAvailable = true;

  for (const listener of updateListeners) {
    try {
      listener();
    } catch (error) {
      MinimalErrorTracker.warn('SYSTEM', 'Service Worker update listener failed', error);
    }
  }
}

function ensureControllerChangeListener(navigatorRef, windowRef) {
  if (
    !navigatorRef ||
    !navigatorRef.serviceWorker ||
    typeof navigatorRef.serviceWorker.addEventListener !== 'function'
  ) {
    return;
  }

  if (controllerChangeListener) {
    return;
  }

  windowTarget = windowRef || windowTarget;
  controllerChangeListener = () => {
    handleControllerChange();
  };

  navigatorRef.serviceWorker.addEventListener('controllerchange', controllerChangeListener);
}

function setupUpdateMonitoring({ navigatorRef, windowRef }) {
  if (!registration) {
    return;
  }

  const currentRegistration = registration;

  currentRegistration.addEventListener('updatefound', () => {
    const newWorker = currentRegistration.installing;
    if (!newWorker) {
      // installing が null の場合でも waiting 状態を次のマイクロタスクで確認
      // （ブラウザの内部状態変化を待つ）
      queueMicrotask(() => {
        if (currentRegistration.waiting) {
          notifyUpdateAvailable();
        }
      });
      return;
    }

    // 状態変化ハンドラーを関数化（既にinstalled状態のケースに対応するため）
    const handleStateChange = () => {
      if (newWorker.state !== 'installed') {
        return;
      }

      if (navigatorRef && navigatorRef.serviceWorker && navigatorRef.serviceWorker.controller) {
        MinimalErrorTracker.info('SYSTEM', 'Service Worker update detected');
        notifyUpdateAvailable();
      } else {
        MinimalErrorTracker.info('SYSTEM', 'Service Worker ready for offline use');
      }
    };

    newWorker.addEventListener('statechange', handleStateChange);

    // 既に installed 状態の場合は即座に処理（タイミング競合対策）
    if (newWorker.state === 'installed') {
      handleStateChange();
    }
  });

  // 起動時に既に waiting 状態のSWがある場合（ページロード時点で更新待機中）
  if (currentRegistration.waiting) {
    notifyUpdateAvailable();
  }

  ensureControllerChangeListener(navigatorRef, windowRef);
}

function setupOnlineStatusListener(windowRef) {
  if (!windowRef || onlineListenersAttached) {
    return;
  }

  windowTarget = windowRef;

  onlineHandler = () => {
    isOnline = true;
    MinimalErrorTracker.info('SYSTEM', 'Connection restored');
  };

  offlineHandler = () => {
    isOnline = false;
    MinimalErrorTracker.info('SYSTEM', 'Connection lost - offline mode');
  };

  windowRef.addEventListener('online', onlineHandler);
  windowRef.addEventListener('offline', offlineHandler);
  onlineListenersAttached = true;
}

/**
 * Service Workerを初期化
 * @param {{ navigatorRef?: Navigator, windowRef?: Window }} [dependencies]
 * @returns {Promise<boolean>} 初期化の成否
 */
export async function initializeServiceWorker({
  navigatorRef = typeof navigator !== 'undefined' ? navigator : undefined,
  windowRef = typeof window !== 'undefined' ? window : undefined,
} = {}) {
  if (!navigatorRef || !('serviceWorker' in navigatorRef)) {
    MinimalErrorTracker.info('SYSTEM', 'Service Worker not supported');
    return false;
  }

  setupOnlineStatusListener(windowRef);
  isOnline = typeof navigatorRef.onLine === 'boolean' ? navigatorRef.onLine : isOnline;

  try {
    navigatorTarget = navigatorRef;
    registration = await navigatorRef.serviceWorker.register('/sw.js');
    MinimalErrorTracker.info('SYSTEM', 'Service Worker registered');

    setupUpdateMonitoring({ navigatorRef, windowRef });
    return true;
  } catch (error) {
    MinimalErrorTracker.warn('SYSTEM', 'Service Worker registration failed', error);
    return false;
  }
}

/**
 * オンライン状態を取得
 * @returns {boolean}
 */
export function getOnlineStatus() {
  return isOnline;
}

/**
 * Service Workerの稼働状態を取得
 * @returns {boolean}
 */
export function isServiceWorkerActive() {
  return Boolean(registration && registration.active);
}

export function onServiceWorkerUpdate(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }

  updateListeners.add(listener);

  if (updateAvailable) {
    try {
      listener();
    } catch (error) {
      MinimalErrorTracker.warn('SYSTEM', 'Service Worker update listener failed', error);
    }
  }

  return () => {
    updateListeners.delete(listener);
  };
}

export function applyServiceWorkerUpdate() {
  return activateWaitingWorker();
}

// 内部関数はテスト用にエクスポートしない（初期化関数のみ公開）
